/* eslint-disable max-depth */
import { defaultErrorCallback, get, post } from '@chilipiper/service/src/api'
import { getDomain, parseQuery } from '@chilipiper/utils'
import moment from 'moment-timezone'
import { store } from '@chilipiper/utils'
import { getSessionId } from '@chilipiper/service/src/session'
import {
  BookerCalendarEvent,
  BookerRescheduleReassign,
  BookerRules,
  CurrentBook,
  InboundRouter,
  MemberInfo,
  Template,
} from '@chilipiper/api-type-def'
import { MeetingRequest, GetInfoDataType } from '@chilipiper/api-type-def'
import { captureException, captureMessage, withScope } from '@chilipiper/sentry'
import { bookMe, reportingService } from '@chilipiper/service'
import type { ApiError } from '@chilipiper/service/src/types'
import { DataType } from '../types'
import { findFirstMember } from '../Finder'
import {
  BookArgs,
  ExtendedLoadQueue,
  LoadAggregatedState,
  LoadQueueData,
  MergeAssigneesInfoType,
  RouterSettings,
} from './types'

export const CP_BAD_SESSION_RELOAD = 'CP_BAD_SESSION_RELOAD'

const domain = getDomain()

export const loadRouterAsync = async (routerName: string) => {
  return get(`book-me/find-router/${routerName}`).json<InboundRouter | null>()
}

export const loadRouter = (
  routerName: string,
  callback: (inboundRouter: InboundRouter | null) => void
) => {
  loadRouterAsync(routerName)
    .then(router => {
      callback(router)
    })
    .catch(e => defaultErrorCallback(e as ApiError))
}

export const loadAvailableEventsAsync = async (data: GetInfoDataType | DataType) => {
  const start = moment('meetingStart' in data ? data.meetingStart : undefined)
  const end = moment('meetingEnd' in data ? data.meetingEnd : undefined)
  const isBeta = parseQuery().isBeta === 'true'

  const weeks = end.diff(start, 'week')

  let mutatedData = { ...data, start: start.valueOf(), end: end.valueOf(), isBeta }
  const apiCalls: Promise<{ slots: BookerCalendarEvent[] }>[] = []

  for (let i = 0; i <= weeks; i++) {
    let eventStart = moment(start).add(i, 'weeks')
    if (!eventStart.hasAlignedHourOffset()) {
      eventStart = eventStart.add(-30, 'minutes')
    }
    let eventEnd = moment(start)
      .add(i + 1, 'weeks')
      .valueOf()

    if (eventEnd > end.valueOf()) {
      eventEnd = end.valueOf()
    }
    mutatedData = { ...data, start: eventStart.valueOf(), end: eventEnd, isBeta }

    apiCalls.push(
      post(`book-me/slots`, {
        json: mutatedData,
      }).json<{ slots: BookerCalendarEvent[] }>()
    )
  }
  const results = await Promise.allSettled(apiCalls)
  const aggregatedResult = results
    .filter(result => result.status === 'fulfilled')
    .flatMap(
      result => (result as PromiseFulfilledResult<{ slots: BookerCalendarEvent[] }>).value.slots
    )
  results
    .filter(result => result.status === 'rejected')
    .forEach(result => {
      const err = (result as PromiseRejectedResult).reason
      // eslint-disable-next-line no-console
      console.log(err)
      defaultErrorCallback(err, {
        tag: {
          name: 'endpoint',
          value: 'book-me/slots',
        },
        transactionName: 'Error loading slots',
      })

      withScope(scope => {
        scope.setFingerprint(['No Available', 'book-me/slots', String(err.statusCode)])
        scope.setTags({
          endpoint: 'book-me/slots',
        })
        scope.setContext('Extra', {
          payload: mutatedData,
          aggregatedResult: aggregatedResult,
        })
        captureException(err)
      })
    })
  return { result: aggregatedResult, payload: mutatedData }
}

export const loadAvailableEvents = (
  data: GetInfoDataType | DataType,
  callback: (
    aggregatedResult: BookerCalendarEvent[],
    data: (GetInfoDataType | DataType) & { isBeta: boolean }
  ) => void
) => {
  loadAvailableEventsAsync(data).then(({ result, payload }) => {
    callback(result, payload)
  })
}

export const loadMembersAsync = (data: DataType) => {
  return post('book-me/info', {
    json: data,
  }).json<MemberInfo[] | undefined | null>()
}

export const reschedule = (
  bookme: BookArgs['bookme'],
  success: BookArgs['success'],
  fault: BookArgs['fault']
) => {
  bookme.source = 'Online'

  /*
   * Group meeting will have assigneesInfo that we should set as assignees, otherwise we should set assigneeId as assignees
   */
  const assignees = bookme.assigneesInfo.length
    ? bookme.assigneesInfo.map(assignee => ({ id: assignee.id ?? '' }))
    : [{ id: bookme.assigneeId ?? '' }]

  const data = getInfoData(bookme, assignees)
  data.start = moment(data.start).valueOf()
  data.end = moment(data.end).valueOf()

  isAvailable(data, fault, result => {
    if (result) {
      post(`reschedule/${domain}`, {
        json: bookme,
      })
        .json<MeetingRequest | BookerRescheduleReassign>()
        .then(booked =>
          success({
            bookme,
            booked,
          })
        )
        .catch(defaultErrorCallback)
    } else {
      fault({ message: 'Assignee is not available', data: data })
    }
  })
}

export const book = ({
  bookme,
  success,
  fault,
  precheck,
  isMeetingApprovalFlow,
  useProspectHack,
}: BookArgs) => {
  bookme.source = 'Online'
  if (bookme.meetingSeriesId) {
    bookMeetingSeries(bookme, success)
  } else if (bookme.assigneeId) {
    bookAssignee({ bookme, success, fault, precheck, isMeetingApprovalFlow, useProspectHack })
  } else {
    bookFirst({ bookme, success, fault, isMeetingApprovalFlow })
  }
}

export const checkAssigneeAvailable = async (
  bookme: BookArgs['bookme'],
  fault: BookArgs['fault'],
  callback: (available: boolean) => void
) => {
  let queue: LoadQueueData | null = null

  if (bookme.queueId) {
    queue = await get(`book-me/queue/${bookme.queueId}`).json<LoadQueueData | null>()
  }

  const assignees =
    bookme.assigneesInfo?.length && queue?.algorithm === 'Group'
      ? bookme.assigneesInfo.map(assignee => ({ id: assignee.id ?? '' }))
      : [{ id: bookme.assigneeId ?? '' }]

  const data = getInfoData(bookme, assignees)
  data.start = moment(data.start).valueOf()
  data.end = moment(data.end).valueOf()
  if (moment(data.start).isBefore(moment())) {
    fault({ message: 'selected date is before now', data: data })
  } else {
    isAvailable(data, fault, callback)
  }
}

const isAvailable = (
  data: GetInfoDataType,
  fault: BookArgs['fault'],
  callback: (available: boolean) => void
) => {
  post('book-me/is-available', {
    json: data,
  })
    .json<{ available: boolean }>()
    .then(result => {
      callback(result.available)
    })
    .catch(message => fault({ message: `book-me/is-available failed with ${message}`, data: data }))
}

const bookMeetingSeries = (bookme: BookArgs['bookme'], success: BookArgs['success']) => {
  bookme.startDate = moment().valueOf() // stub
  bookme.endDate = moment().valueOf() // stub
  post('book-meeting-series', {
    json: bookme,
  })
    .json<any>()
    .then(booked => {
      success({ bookme, booked })
    })
    .catch(defaultErrorCallback)
}

const bookAssignee = ({
  bookme,
  success,
  fault,
  precheck,
  isMeetingApprovalFlow,
  useProspectHack,
}: BookArgs) => {
  const postBook = () => {
    const bookerInfo = getInfoData(bookme, [{ id: bookme.assigneeId ?? '' }])
    bookMe
      .lastMomentCheck(bookerInfo)
      .then(result => {
        if (result.busy) {
          fault({
            message: 'last-moment-check failed with busy',
            data: bookme,
          })
        } else {
          post(
            isMeetingApprovalFlow ? 'booker/request' : `book/${getSessionId()}?domain=${domain}`,
            {
              json: bookme,
            }
          )
            .json<MeetingRequest | BookerRescheduleReassign>()
            .then(booked => {
              if ('error' in booked && booked.error !== undefined) {
                return fault({
                  message: booked.error,
                  data: { bookme, booked },
                })
              }

              if (!useProspectHack) {
                return success({ bookme, booked })
              }

              return get(`online/prospect-by-template/${domain}/${bookme.template}`)
                .json()
                .then(response => {
                  if (response && typeof response === 'object' && (response as any).id) {
                    return post(
                      `online/prospect-by-form/${domain}/${(response as any).id}/${
                        bookme.assigneeId
                      }?templateId=${bookme.template}${
                        useProspectHack.campaignId
                          ? `&campaignId=${useProspectHack.campaignId}`
                          : ''
                      }${useProspectHack.status ? `&memberStatus=${useProspectHack.status}` : ''}`,
                      {
                        json: {
                          values: {
                            timeZone: [bookme.timezone],
                            email: [bookme.prospect],
                          },
                        },
                      }
                    )
                  }
                })
                .then(() => {
                  success({ bookme, booked })
                })
            })
            .catch(body => fault({ message: body.error, data: bookme }))
        }
      })
      .catch(defaultErrorCallback)
  }
  if (precheck) {
    checkAssigneeAvailable(bookme, fault, available => {
      if (available) {
        postBook()
      } else {
        fault({ message: 'assignee not available', data: bookme })
      }
    })
  } else {
    postBook()
  }
}

const bookFirst = ({ bookme, success, fault, isMeetingApprovalFlow }: BookArgs) => {
  loadQueue(
    bookme,
    queue => {
      const data = getInfoData(bookme, queue?.members)
      loadAvailableEvents(data, async (events, data) => {
        if (events && events.length !== 0) {
          const busyEvents = events.filter(e => e.start === moment(bookme.startDate).valueOf())
          const busyIds = busyEvents.flatMap(e => e.assigneeIds)
          try {
            const assignees = await post('book-me/info', {
              json: data,
            }).json<MemberInfo[] | undefined>()
            if (assignees === undefined || assignees.length === 0) {
              fault({ message: 'book-me/info did not return assignees', data: bookme })
            } else {
              const members = queue ? mergeAssigneesInfo(queue, assignees) : []

              const first = findFirstMember(queue, busyIds)
              if (first) {
                const firstMember = members.find(m => m.id === first.id)
                bookme.assigneeId = firstMember?.id
                const firstInQueue = findFirstMember(queue)
                bookme.orderPositions = {
                  firstInQueueId: firstInQueue?.id,
                }
                bookme.assigneesInfo = assignees || []
                bookme.bookerInfo = { assigneesGroupIds: [], roomsIds: [], ...data }
                bookAssignee({ bookme, success, fault, isMeetingApprovalFlow })
              } else {
                fault({
                  message: 'could not get first assignee',
                  data: bookme,
                })
              }
            }
          } catch (e) {
            defaultErrorCallback(e as ApiError)
          }
        } else {
          fault({ message: 'no available events were returned', data: bookme })
        }
      })
    },
    fault
  )
}

const mergeAssigneesInfo = (
  queue: ExtendedLoadQueue,
  assignees: MemberInfo[]
): MergeAssigneesInfoType => {
  return queue.members.map(member => {
    const info = assignees.find(a => a.id === member.id)
    if (info === undefined) {
      return { ...member, available: true }
    }
    return {
      ...member,
      order: info.order,
      accessible: info.accessible,
      onVacation: queue.algorithm !== 'Ownership' ? info.onVacation : undefined,
      available: true,
    }
  })
}

const getInfoData = (bookme: BookArgs['bookme'], members?: { id: string }[]): GetInfoDataType => {
  const guests = (bookme.invitees || []).filter((i: any) => i.mandatory).map((i: any) => i.email)
  return {
    assigneesIds: members?.map(m => m.id) ?? [],
    start: moment(bookme.startDate).valueOf(),
    end: moment(bookme.endDate).valueOf(),
    timezone: bookme.timezone,
    step:
      bookme.duration && (bookme.duration <= 15 || bookme.duration === 45) ? bookme.duration : 30,
    slot: bookme.duration ?? 0,
    queueId: bookme.queueId,
    guests: guests,
    template: bookme.template ?? '',
    withBuffers: true,
    routeId: bookme.routeId,
    roomsIds: bookme.roomId ? [bookme.roomId] : undefined,
  }
}

export const loadQueue = async (
  bookMeeting: any,
  callback: (queue?: ExtendedLoadQueue) => void,
  fault?: BookArgs['fault']
) => {
  try {
    const queue = await get(`book-me/queue/${bookMeeting.queueId}`).json<LoadQueueData | null>()
    if (bookMeeting?.id && queue?.algorithm === 'Ownership') {
      try {
        const rules = await post('book-me/rules', {
          json: {
            id: bookMeeting.id,
            isLead: bookMeeting.isLead,
            queues: [bookMeeting.queueId],
          },
        }).json<BookerRules[]>()
        rules.forEach(entity => {
          callback({ ...queue, success: entity.success, owners: entity.owners })
        })
      } catch (error) {
        defaultErrorCallback(error as ApiError)
      }
    } else {
      callback(queue ?? undefined)
    }
  } catch (error) {
    onError(error)
    fault?.({
      message: 'could not get queue',
      data: bookMeeting,
    })
  }
}

export const loadBookMe = (
  callback: (currentBook?: CurrentBook) => void,
  fault?: BookArgs['fault'],
  tenantDomain?: string
) => {
  const handleError = (err: any) => {
    onError(err)
    // bad place to kill session
    bookMe
      .removeGuestSession()
      .then(() => {
        fault?.({ message: 'kill the session' })
        // refresh to generate new session, otherwise we get endless spinner
        const hasReloaded = store.session.get(CP_BAD_SESSION_RELOAD)
        if (!store.isFake() && !hasReloaded && !window.location.href.includes('not-available')) {
          store.session.set(CP_BAD_SESSION_RELOAD, true)
          window.location.reload()
        }
      })
      .catch(defaultErrorCallback)
  }
  bookMe
    .current(getSessionId(), tenantDomain ?? domain)
    .then(result => {
      if (result?.bookResult?.error) {
        fault?.({
          message: result?.bookResult?.error,
          data: result,
        })
      } else {
        callback(result ?? undefined)
      }
    })
    .catch(handleError)
}

const filterMembersByOwnerShip = (queue?: ExtendedLoadQueue) => {
  if (queue?.algorithm === 'Ownership' && Boolean(queue?.owners?.length)) {
    return queue?.members.filter(member => queue.owners?.includes(member.id))
  }
  return queue?.members
}

// TODO :Use Async function instead
export const loadAggregated = (
  callback: (state: Omit<LoadAggregatedState, 'bookme'> & { bookme?: CurrentBook }) => void,
  optionalData: any = null,
  fault?: BookArgs['fault'],
  tenantDomain?: string
) => {
  const subdomain = tenantDomain ?? domain
  let templateLoaded = false
  let queueLoaded = false
  let settingsLoaded = false
  const state: LoadAggregatedState = {}

  const sendResult = (result: LoadAggregatedState, bookme?: CurrentBook) => {
    if (templateLoaded && queueLoaded && settingsLoaded) {
      callback({ ...result, bookme })
    }
  }

  const loadTemplate = (bookme?: CurrentBook) => {
    const template = optionalData?.template ? optionalData.template : bookme?.bookMeeting?.template
    get(`book-me/template/${template}`)
      .json<Template>()
      .then(template => {
        state.template = template ?? {}
        templateLoaded = true
        sendResult(state, bookme)

        if (!template) {
          withScope(scope => {
            scope.setContext('Tenant', {
              Domain: subdomain,
              'Booking link': bookme?.bookMeeting?.bookingUrl,
              Api: `book-me/template`,
            })
            captureMessage('Template not found')
          })
        }
      })
      .catch(onError)
  }

  const loadQueueClosure = (bookme?: CurrentBook) => {
    if (bookme?.bookMeeting?.queueId) {
      loadQueue(bookme.bookMeeting, queue => {
        state.members = filterMembersByOwnerShip(queue)
        state.queue = queue
        queueLoaded = true
        sendResult(state, bookme)
      })
    } else {
      queueLoaded = true
      sendResult(state, bookme)
    }
  }

  const loadRouterSettings = (bookme?: CurrentBook) => {
    if (bookme?.bookMeeting?.routeId) {
      get(`book-me/router-settings-by-route/${bookme.bookMeeting.routeId}`)
        .json<RouterSettings>()
        .then(settings => {
          state.routerSettings = settings
          settingsLoaded = true
          sendResult(state, bookme)
        })
        .catch(onError)
    } else {
      settingsLoaded = true
      sendResult(state, bookme)
    }
  }

  const loadReport = (bookme: CurrentBook) => {
    const secret = window?.location?.hash.replace('#', '')
    return reportingService
      .getReport(bookme.bookResult?.reportId ?? '', subdomain, secret)
      .then(report => {
        const { assigneeId, template, queueId, bookerInfo } = report.bookRequest

        return {
          ...bookme,
          bookMeeting: {
            ...bookme.bookMeeting,
            bookerInfo,
            assigneeId,
            template,
            queueId,
          },
        }
      })
  }

  loadBookMe(
    (bookme?: CurrentBook) => {
      // Check if is rescheduling a meeting we should rely on the report
      if (bookme?.isBooked) {
        loadReport(bookme).then((bookme: CurrentBook) => {
          loadTemplate(bookme)
          loadQueueClosure(bookme)
          loadRouterSettings(bookme)
        })
      } else {
        loadTemplate(bookme)
        loadQueueClosure(bookme)
        loadRouterSettings(bookme)
      }
    },
    fault,
    subdomain
  )
}

const onError = (err: any) => {
  console.error('chilipiper >> ', JSON.stringify(err))
  defaultErrorCallback(err)
}
